import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import type { BadgedButtonChipProps, BadgedButtonProps } from './BadgedButton.types';

/**
 * The badged button's outline: the chip's own purple, so the chip reads as
 * belonging to THIS button rather than floating between two.
 *
 * A className rather than a Button prop because it reaches the border on both
 * platforms as-is: the Button draws its border on the Pressable itself (the
 * variant's `border-secondary-200`), not on a wrapper — the bevel and glass
 * layers are borderless `absolute inset-0` children — and `className` merges
 * LAST through twMerge, on web and through NativeWind on native alike. The
 * hover twin is there because the secondary variant re-colours its border on
 * hover (`enabled:hover:border-secondary-300`), which would otherwise turn the
 * outline grey under a mouse or an iPad pointer. `-500` is the palette
 * inversion's fixed point, so it is the same purple in light and dark.
 */
const BADGED_OUTLINE = 'border-primary-500 enabled:hover:border-primary-500';

/**
 * The "Last used" chip — ONE look for everywhere a sign-in method is marked:
 * on a {@link BadgedButton}'s top edge, and in the email field's label row
 * (the shared Input's `labelTrailing`). Exported so no call site can re-pick
 * the colour, style or size and fork it.
 */
export function BadgedButtonChip({ label, disabled, testID }: BadgedButtonChipProps) {
  return (
    <Badge
      colour="primary"
      style="solid"
      size="xxs"
      disabled={disabled}
      testID={testID}
      accessibilityLabel={label}>
      {label}
    </Badge>
  );
}

/**
 * A Button with a small chip seated on its top edge — the "Last used" hint on
 * the Google / Apple sign-in buttons on web and mobile. The badged button also
 * takes the chip's purple as its border.
 *
 * The chip sits OUTSIDE the button's box on purpose: inside, it competes with
 * the label for the row's width (the old web version inlined it after the
 * text; mobile appended " · Last used" to the label) and both wrapped or
 * truncated at narrow widths. Straddling the border reads as an annotation
 * of the button, not part of its label.
 *
 * Placement: centred ON the top border — the 20px chip rises 10px above it —
 * and inset 16px from the right edge, clear of the corner's curve. It never
 * takes a tap (`pointer-events-none`, which NativeWind maps to `pointerEvents`
 * on native): the half that overlaps the button must press the button.
 *
 * Every Button prop passes straight through; `fullWidth` also stretches the
 * wrapper so the block drops into the same stack the plain Button did. With no
 * `badge` it renders the plain Button, outline included.
 */
export function BadgedButton({
  badge,
  containerClassName,
  fullWidth,
  className,
  ...button
}: BadgedButtonProps) {
  return (
    <View
      className={cn('relative', fullWidth ? 'w-full' : 'self-start', containerClassName)}>
      <Button
        fullWidth={fullWidth}
        className={cn(badge && BADGED_OUTLINE, className)}
        {...button}
      />
      {badge ? (
        <View className="pointer-events-none absolute -top-2.5 right-4 z-10">
          <BadgedButtonChip
            label={badge}
            disabled={button.disabled}
            testID={subTestID(button.testID, 'badge')}
          />
        </View>
      ) : null}
    </View>
  );
}
